import { useState, useEffect } from "react";
import { FaCar, FaMapMarkedAlt, FaTree, FaHome } from "react-icons/fa";
import "../styles/about.scss";
import PageHeader from "../components/PageHeader/PageHeader";
import { useGetMilestonesQuery } from "../features/api/adminApiSlice";
import Section from "../components/ui/Section";
import UpcomingTripsWidget from "./UpcomingTripsWidget";

const slideshowImages = [
  "https://images.pexels.com/photos/13248795/pexels-photo-13248795.jpeg",
  "https://images.pexels.com/photos/32162192/pexels-photo-32162192.jpeg",
  "https://images.pexels.com/photos/15209797/pexels-photo-15209797.jpeg",
  "https://images.pexels.com/photos/8465718/pexels-photo-8465718.jpeg",
];

export default function About() {
  const [currentImgIndex, setCurrentImgIndex] = useState(0);
  const { data: serverStats, isLoading } = useGetMilestonesQuery();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImgIndex(
        (prevIndex) => (prevIndex + 1) % slideshowImages.length
      );
    }, 4500);
    return () => clearInterval(timer);
  }, []);

  const milestones = [
    {
      value: serverStats?.milesDriven || "0",
      label: "Miles Driven",
      icon: <FaMapMarkedAlt />,
    },
    {
      value: serverStats?.nationalParks || "0",
      label: "National Parks",
      icon: <FaTree />,
    },
    {
      value: serverStats?.smallTowns || "0",
      label: "Small Towns",
      icon: <FaHome />,
    },
    {
      value: serverStats?.smallCar || "1",
      label: "Small Car",
      icon: <FaCar />,
    },
  ];

  return (
    <main className="about-page">
      <Section>
        <PageHeader
          kicker="Our Manifest"
          title="Mini Miles Adventures"
          subtitle="Following quiet roads and the unexpected places they lead."
        />

        <div className="about-container">
          {/* LEFT COLUMN: Narrative & Media */}
          <div className="about-story fuel-fade-in">
            <p className="lead-paragraph">
              Mini Miles Adventures explores national parks, vibrant cities, and
              quiet backroads through the highways that connect them—all
              captured from the unique perspective of a small car.
            </p>
            <div className="about-slideshow">
              {slideshowImages.map((imgUrl, idx) => (
                <div
                  key={idx}
                  className={`about-slideshow__slide ${
                    idx === currentImgIndex ? "active" : ""
                  }`}
                  style={{ backgroundImage: `url(${imgUrl})` }}
                />
              ))}
              <div className="about-slideshow__overlay" />
            </div>
            <div className="text-body">
              <p>
                We believe travel isn’t just about pinning a spot on a map, but
                how the road shapes the place itself. From shifting horizons and
                unexpected weather detours to the quiet, unscripted moments in
                between, each journey focuses on the pure experience of getting
                there.
              </p>
              <p>
                This channel isn’t about rushing to catch a tourist viewpoint.
                It’s a declaration that the journey is the destination. It’s
                about slowing down to see what is truly possible with less, one
                quiet mile at a time.
              </p>
            </div>
            <blockquote className="about-quote">
              "The best roads aren't always the fastest lines on a map; they are
              the ones where you don't mind taking a wrong turn."
            </blockquote>
          </div>

          {/* RIGHT COLUMN: Live Milestones Sidebar Panel */}
          <div
            className="about-sidebar fuel-fade-in"
            style={{ animationDelay: "0.1s" }}
          >
            <div className="milestones-card">
              <h3>Our Journey Tracker</h3>
              <p>
                A live breakdown of our small-car expeditions across the
                country.
              </p>
              <div className="milestones-grid">
                {isLoading ? (
                  <div className="milestones-skeleton-text">
                    Syncing data...
                  </div>
                ) : (
                  milestones.map((stat, i) => (
                    <div key={i} className="stat-chip">
                      <span className="stat-chip__icon" aria-hidden="true">
                        {stat.icon}
                      </span>
                      <div className="stat-chip__data">
                        <strong>{stat.value}</strong>
                        <span>{stat.label}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="about-contact-zone">
                <a href="/contact" className="fuel-primary-btn contact-btn">
                  Send a Road Message
                </a>
                <span className="contact-hint">
                  Have a scenic route recommendation? Drop us a line!
                </span>
              </div>
            </div>

            {/* 🟢 NEW: INJECTED UPCOMING EXPEDITIONS WORKSPACE WIDGET */}
            <UpcomingTripsWidget />
          </div>
        </div>
      </Section>
    </main>
  );
}
